import { ExperimentPlatform } from '../types/experiment.types';

/** Non-Google-prefixed catalog codes that still belong to the Google-Ads
 *  experiment flow. Shopify and Manual Upload are catalog sources paired
 *  with Google Ads — never with Meta — so they ride the same mappings as
 *  the `GOOGLE_*` prefix. */
const GOOGLE_ALIAS_CODES = new Set(['SHOPIFY', 'MANUAL_UPLOAD']);

/**
 * Map a backend connector code to the platform identifier used by the
 * config-service KPI API (`?platform=google|meta`). Codes outside the
 * known prefixes return `null` — callers should treat that as "no platform
 * selected yet" and avoid firing the KPI query.
 *
 * `SHOPIFY` and `MANUAL_UPLOAD` are treated as Google-Ads catalog sources
 * (see `resolveCatalogSource` / `INPUT_SOURCES` in onboarding), so KPI
 * lookups for those sources should ride the Google KPI catalog.
 */
export function platformForConnector(code: string | null): 'google' | 'meta' | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  if (upper.startsWith('GOOGLE_') || GOOGLE_ALIAS_CODES.has(upper)) return 'google';
  if (upper.startsWith('META_') || upper.startsWith('FACEBOOK_')) return 'meta';
  return null;
}

/** Ad-platform tokens accepted by the experiment-service create-draft body. */
export type AdPlatform = 'GOOGLE_ADS' | 'META_ADS';

/**
 * Map a connector code to the `adPlatform` token sent in the create-draft
 * body. Google connectors (e.g. `GOOGLE_MERCHANT`, `GOOGLE_ADS`),
 * `SHOPIFY`, and `MANUAL_UPLOAD` all collapse to `GOOGLE_ADS` — Shopify
 * and Manual-Upload catalogs are paired with Google Ads in this product.
 * Meta/Facebook connectors collapse to `META_ADS`. Returns `null` for
 * unknown prefixes so callers can guard against firing the POST.
 */
export function adPlatformForConnector(code: string | null): AdPlatform | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  if (upper.startsWith('GOOGLE_') || GOOGLE_ALIAS_CODES.has(upper)) return 'GOOGLE_ADS';
  if (upper.startsWith('META_') || upper.startsWith('FACEBOOK_')) return 'META_ADS';
  return null;
}

/**
 * Map the `adPlatform` token returned on an experiment DTO to the UI
 * `ExperimentPlatform` enum that drives the table's PlatformBadge. Tolerant
 * of any `GOOGLE_*` / `META_*` / `FACEBOOK_*` value so rows still render the
 * correct logo if the backend ships a more specific token later.
 */
export function experimentPlatformFromAdPlatform(
  value: string | null | undefined,
): ExperimentPlatform | null {
  if (!value) return null;
  const upper = value.toUpperCase();
  if (upper.startsWith('GOOGLE')) return ExperimentPlatform.GOOGLE;
  if (upper.startsWith('META') || upper.startsWith('FACEBOOK')) return ExperimentPlatform.META;
  return null;
}
